import React, {useEffect, useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';
import {
  useFetchMultiPayment,
  usePath,
  usePaymentsCart,
  usePaymentSettings,
} from '../../../hooks';
import {PATHS} from 'routes/constants';
import {APP_PAGES} from 'utils/pages';
import Page from '../../../components/Page';
import PaymentForm from '../../../components/PaymentForm';
import {AddonPaymentForm} from 'components/AddonPaymentForm';
import {PAYMENT_PROVIDERS} from '@chekin/core';
import {POSTHOG_EVENTS} from 'analytics/posthog/constants';
import {usePostHogTrack} from 'analytics/posthog';

function PaymentFormView() {
  const {t} = useTranslation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();
  const location = useLocation();
  const {paymentProvider} = usePaymentSettings();
  const {trackEvent} = usePostHogTrack();
  const {paymentsCart, isPaymentsCartLoading} = usePaymentsCart({
    defaultDeferredPayments: location.state?.pay_later_payments,
  });

  const {data: multiPayment, isInitialLoading: isMultiPaymentLoading} =
    useFetchMultiPayment({
      id: location.state?.paymentId,
    });

  const isFromCheckout = location.state?.from === APP_PAGES.checkout;
  const navBarTitle =
    location.state?.from === APP_PAGES.checkout
      ? t('check_out_short')
      : t('booking_payments');

  const onSuccessPaymentForm = useCallback(() => {
    if (isFromCheckout) {
      navigate(location.state?.redirectAfterPaidPath);
    } else {
      navigate(getFullPath(PATHS.payments.ordersHistory));
    }
    trackEvent(POSTHOG_EVENTS.PAYMENT_COMPLETED, {
      paymentProvider: paymentProvider,
      total_amount_to_pay: paymentsCart?.total_amount_to_pay || 0,
    });
  }, [
    isFromCheckout,
    navigate,
    location.state?.redirectAfterPaidPath,
    getFullPath,
    paymentsCart?.total_amount_to_pay,
    paymentProvider,
    trackEvent,
  ]);

  useEffect(() => {
    if (!location.state?.payments?.length) {
      navigate(getFullPath(PATHS.payments.main));
    }
  }, [getFullPath, location.state?.payments?.length, navigate]);

  useEffect(() => {
    if (
      paymentProvider === PAYMENT_PROVIDERS.addon &&
      !isMultiPaymentLoading &&
      !multiPayment?.id
    ) {
      navigate(getFullPath(PATHS.payments.main));
    }
  }, [getFullPath, isMultiPaymentLoading, multiPayment?.id, navigate, paymentProvider]);

  return (
    <Page
      isSubHeaderVisible
      onBackButtonClick={() => {
        navigate(getFullPath(PATHS.payments.main));
      }}
      hideSubPanelBackButton
      title={t('payment')}
      subtitle={t('enter_your_card_details')}
      navBarTitle={navBarTitle}
      className="payment-form-view"
      isLoading={!paymentProvider}
    >
      {paymentProvider === PAYMENT_PROVIDERS.stripe ? (
        <PaymentForm
          paymentsCart={paymentsCart}
          isLoading={isPaymentsCartLoading}
          onSuccess={onSuccessPaymentForm}
        />
      ) : (
        <AddonPaymentForm
          paymentsCart={paymentsCart}
          isLoading={isPaymentsCartLoading || isMultiPaymentLoading}
          onSuccess={onSuccessPaymentForm}
          multiPayment={multiPayment}
        />
      )}
    </Page>
  );
}

export {PaymentFormView};
